import type { LevelAssessmentModel, ModelAxis } from "@shared/labs/level-assessment-model";
import { useEffect, useMemo, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { rpcClient } from "@/lib/rpc";
import { useActiveModel } from "../active-model-context";

/** Axis id → prompt markdown, or `null` when the axis exists in the model but
 *  has no prompt row yet (partial bootstrap). */
type PromptMap = Record<string, string | null>;

function describeError(err: unknown): { title: string; detail: string } {
  const e = err as { status?: number; code?: string; message?: string } | null;
  if (e && (e.status === 403 || e.code === "FORBIDDEN")) {
    return {
      title: "Sem acesso aos prompts",
      detail:
        "Ver os prompts deste modelo exige perfil admin ou acesso ao Labs. Os outros eixos do modelo carregam com o mesmo acesso.",
    };
  }
  return {
    title: "Falha ao carregar prompts",
    detail: e instanceof Error ? e.message : "Erro inesperado.",
  };
}

export function PromptsEditor() {
  const { modelId } = useActiveModel();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [model, setModel] = useState<LevelAssessmentModel | null>(null);
  const [prompts, setPrompts] = useState<PromptMap | null>(null);
  const [scope, setScope] = useState<string>("cross");
  const [activeAxis, setActiveAxis] = useState<string | null>(null);
  const lastModelId = useRef(modelId);

  // The axes of the scope being viewed. Drives the tab strip + which prompt
  // is shown. Cross is schema-guaranteed non-empty; a discipline likewise.
  const axes: ModelAxis[] = useMemo(() => {
    if (!model) return [];
    return scope === "cross"
      ? model.crossAxes
      : (model.disciplines.find((d) => d.id === scope)?.axes ?? []);
  }, [model, scope]);

  useEffect(() => {
    // Switching models must never fetch a discipline scope that belonged to the
    // previous model (it would 403 as an unknown scope) — reset to cross first.
    if (lastModelId.current !== modelId && scope !== "cross") {
      lastModelId.current = modelId;
      setScope("cross");
      return; // setScope re-runs this effect with scope="cross".
    }
    lastModelId.current = modelId;

    let cancelled = false;
    setLoading(true);
    setModel(null);
    setPrompts(null);
    setActiveAxis(null);
    setError(null);
    (async () => {
      try {
        const [modelRes, promptsRes] = await Promise.all([
          rpcClient.labs.levelAssessmentModelGet({ modelId }),
          rpcClient.labs.levelAssessmentAxisPromptsGet({ modelId, scope }),
        ]);
        if (cancelled) return;
        const m = (modelRes as { model: LevelAssessmentModel }).model;
        const loaded = promptsRes as PromptMap;
        const scopeAxes =
          scope === "cross" ? m.crossAxes : (m.disciplines.find((d) => d.id === scope)?.axes ?? []);
        setModel(m);
        setPrompts(loaded);
        setActiveAxis(scopeAxes[0]?.id ?? null);
      } catch (e) {
        if (cancelled) return;
        setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId, scope]);

  if (loading) {
    return (
      <div style={{ padding: 24, color: "var(--r-ink-muted)", fontSize: 13 }}>
        Carregando prompts…
      </div>
    );
  }
  if (error) {
    const { title, detail } = describeError(error);
    return (
      <div
        data-testid="prompts-editor-error"
        style={{ padding: 24, color: "var(--r-coral, #f3354b)", fontSize: 13, lineHeight: 1.5 }}
      >
        <strong>{title}</strong>
        <div style={{ marginTop: 4, color: "var(--r-ink-muted)" }}>{detail}</div>
      </div>
    );
  }
  if (!model || !prompts || !activeAxis) {
    return (
      <div style={{ padding: 24, color: "var(--r-ink-muted)", fontSize: 13 }}>
        Modelo sem eixos para exibir.
      </div>
    );
  }

  const activePrompt = prompts[activeAxis] ?? null;

  return (
    <div data-testid="prompts-editor">
      <div
        style={{
          fontSize: 12,
          color: "var(--r-ink-muted)",
          lineHeight: 1.5,
          marginBottom: 16,
        }}
      >
        Um prompt por eixo no modelo <strong>{model.name}</strong>. Cada um recebe apenas os sinais
        roteados àquele eixo (mais os priors). Os prompts são{" "}
        <strong style={{ color: "var(--r-ink)" }}>armazenados no modelo (DB)</strong> e gerados a
        partir da matriz de sinais. Esta aba é{" "}
        <strong style={{ color: "var(--r-ink)" }}>somente leitura</strong>: para mudar um prompt,
        edite o roteamento em <strong>Sinais por eixo</strong> e clique em{" "}
        <strong>Regenerar prompts</strong> — a matriz é a fonte de verdade.
      </div>

      {/* Scope selector — only for models with disciplines. */}
      {model.disciplines.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: "var(--r-ink-muted)", fontWeight: 600 }}>Escopo</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            data-testid="prompts-editor-scope"
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid var(--r-ink-faint, #e9e3d8)",
              background: "var(--r-bg, #fffaf3)",
              color: "var(--r-ink)",
            }}
          >
            <option value="cross">Cross</option>
            {model.disciplines.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Axis tab strip */}
      <div
        role="tablist"
        aria-label="Eixo"
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--r-ink-faint, #e9e3d8)",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        {axes.map((axis: ModelAxis) => {
          const isActive = axis.id === activeAxis;
          const isMissing = prompts[axis.id] == null;
          return (
            <button
              key={axis.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveAxis(axis.id)}
              data-testid={`prompts-tab-${axis.id}`}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "var(--r-ink)" : "var(--r-ink-muted)",
                borderBottom: isActive ? "2px solid var(--r-ink)" : "2px solid transparent",
                cursor: "pointer",
                marginBottom: -1,
              }}
            >
              {axis.label}
              {isMissing && (
                <span
                  role="img"
                  aria-label="eixo sem prompt (não bootstrapado)"
                  title="sem prompt — regenere os prompts"
                  style={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--r-amber, #b07811)",
                    marginLeft: 6,
                    verticalAlign: "middle",
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Read-only view for the active axis */}
      <div className="card" style={{ padding: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 12, color: "var(--r-ink-muted)" }}>
            Prompt do eixo{" "}
            <strong style={{ color: "var(--r-ink)" }}>
              {axes.find((a) => a.id === activeAxis)?.label ?? activeAxis}
            </strong>{" "}
            (escopo {scope}) —{" "}
            <code style={{ fontSize: 11 }}>
              axis:{scope}:{activeAxis}
            </code>
          </div>
        </div>
        {activePrompt == null ? (
          <div
            data-testid={`prompts-missing-${activeAxis}`}
            style={{
              padding: "20px 16px",
              border: "1px dashed var(--r-amber, #b07811)",
              borderRadius: 8,
              fontSize: 13,
              color: "var(--r-ink-muted)",
              lineHeight: 1.55,
            }}
          >
            Este eixo ainda não tem prompt no modelo (bootstrap parcial). Vá em{" "}
            <strong>Sinais por eixo</strong> e clique em <strong>Regenerar prompts</strong> para
            gerá-lo a partir da matriz.
          </div>
        ) : (
          <Textarea
            value={activePrompt}
            readOnly
            rows={32}
            spellCheck={false}
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              fontSize: 12,
              lineHeight: 1.55,
            }}
            data-testid={`prompts-textarea-${activeAxis}`}
          />
        )}
      </div>
    </div>
  );
}
